import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import Modal from '@components/common/Modal'
import projectTeamService from '@services/projectTeamService'
import RoleAssignment from './RoleAssignment'
import './TeamManagementModal.css'

/**
 * TeamManagementModal Component
 *
 * Modal for managing team member assignments to project roles
 * Allows assigning users to roles like PM, Executive, Auditor, etc.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Function} props.onClose - Close handler
 * @param {number} props.projectId - Project ID
 * @param {Function} props.onSuccess - Success callback with updated team members
 */
const TeamManagementModal = ({ isOpen, onClose, projectId, onSuccess }) => {
  const [projectRoles, setProjectRoles] = useState([])
  const [assignments, setAssignments] = useState({}) // { role_id: user_id }
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (isOpen && projectId) {
      fetchProjectRoles()
    }
  }, [isOpen, projectId])

  const fetchProjectRoles = async () => {
    try {
      setLoading(true)
      setError(null)

      // Fetch project roles with current assignments
      const response = await projectTeamService.getProjectRoles(projectId)
      setProjectRoles(response)

      // Build assignments map from current assignments
      const currentAssignments = {}
      response.forEach((role) => {
        if (role.assigned_user) {
          currentAssignments[role.role_id] = role.assigned_user.user_id
        }
      })
      setAssignments(currentAssignments)
    } catch (err) {
      console.error('Failed to fetch project roles:', err)
      setError('Failed to load project roles. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleAssignmentChange = (roleId, userId) => {
    setAssignments((prev) => {
      const newAssignments = { ...prev }
      if (userId) {
        newAssignments[roleId] = userId
      } else {
        delete newAssignments[roleId]
      }
      return newAssignments
    })
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)

      // Convert assignments object to array format
      const assignmentsArray = Object.entries(assignments).map(([roleId, userId]) => ({
        role_id: parseInt(roleId),
        user_id: parseInt(userId),
      }))

      // Sync assignments
      const response = await projectTeamService.syncTeamMembers(projectId, assignmentsArray)

      // Call success callback
      // projectTeamService.syncTeamMembers already extracts response.data
      if (onSuccess) {
        onSuccess(response)
      }

      // Close modal
      onClose()
    } catch (err) {
      console.error('Failed to save team assignments:', err)
      setError('Failed to save team assignments. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    // Reset to original assignments
    const originalAssignments = {}
    projectRoles.forEach((role) => {
      if (role.assigned_user) {
        originalAssignments[role.role_id] = role.assigned_user.user_id
      }
    })
    setAssignments(originalAssignments)
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title="Manage Team Members"
      size="medium"
      closeOnBackdrop={false}
    >
      <div className="team-management-modal">
        {loading ? (
          <div className="team-management-modal__loading">
            <div className="spinner"></div>
            <p>Loading roles...</p>
          </div>
        ) : error ? (
          <div className="team-management-modal__error">
            <p>{error}</p>
            <button className="btn btn--secondary btn--small" onClick={fetchProjectRoles}>
              Retry
            </button>
          </div>
        ) : (
          <>
            <div className="team-management-modal__info">
              Assign team members to each role. <strong>Project Manager</strong> and <strong>Executive</strong> are required for approval workflow.
            </div>

            {projectRoles.length === 0 ? (
              <div className="team-management-modal__empty">
                <p>No roles associated with this project.</p>
                <p className="team-management-modal__empty-hint">Add roles to the project first in the "Associated Roles" tab.</p>
              </div>
            ) : (
              <div className="team-management-modal__list">
                {projectRoles.map((role) => (
                  <RoleAssignment
                    key={role.role_id}
                    projectId={projectId}
                    role={role}
                    selectedUserId={assignments[role.role_id]}
                    onChange={(userId) => handleAssignmentChange(role.role_id, userId)}
                  />
                ))}
              </div>
            )}

            <div className="team-management-modal__footer">
              <button
                className="btn btn--secondary"
                onClick={handleCancel}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="btn btn--primary"
                onClick={handleSave}
                disabled={saving || projectRoles.length === 0}
              >
                {saving ? 'Saving...' : 'Save Team Assignments'}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}

TeamManagementModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  projectId: PropTypes.number.isRequired,
  onSuccess: PropTypes.func,
}

export default TeamManagementModal
